import {Component, OnInit, QueryList, ViewChildren, HostListener, OnDestroy} from '@angular/core';
import { CustomerService } from 'src/app/shared/services/customer.service';
import { ActivatedRoute, Router } from '@angular/router';
import {plainToInstance} from 'class-transformer';
import { GroupDealerComponent } from 'src/app/pages/customer/components/group-dealer/group-dealer.component';
import {VehicleBrand} from 'src/app/domain/core/vehicle-brand.model';
import {LegalEntity} from 'src/app/domain/core/legal-entity.model';
import {environment} from 'src/environments/environment';
import {Country} from 'src/app/domain/core/country.model';
import {PreferredDealerComponent} from 'src/app/pages/customer/components/preferred-dealer/preferred-dealer.component';
import {ActionButtonsComponent} from 'src/app/pages/customer/components/action-buttons/action-buttons.component';
import {CommonModule} from '@angular/common';
import {AppState} from '../../shared/utils/AppState';
import {PageComponent, PageSize} from '../../shared/components/page/page.component';
import {CommunicationComponent} from './components/communication/communication.component';
import {ProfilingComponent} from './components/profiling/profiling.component';
import {DataSharingComponent} from './components/data-sharing/data-sharing.component';
import {CustomerClientRequest} from '../../domain/data/customer-client-request.data';
import {CustomerClientResponse, DealerWithGroup} from '../../domain/data/customer-client-response.data';
import {PreferredDealerCustomerUpdateComponent} from './components/preferred-dealer-customer-update/preferred-dealer-customer-update.component';
import {NewConsentVersionDisclaimerComponent} from './components/new-consent-version-disclaimer/new-consent-version-disclaimer.component';
import {LoadingSpinnerComponent} from '../../shared/components/loading-spinner/loading-spinner.component';
import {BlacklistComponent} from './components/blacklist/blacklist.component';

@Component({
  selector: 'app-customer',
  templateUrl: './customer.component.html',
  imports: [
    CommonModule,
    GroupDealerComponent,
    PreferredDealerComponent,
    ActionButtonsComponent,
    PageComponent,
    CommunicationComponent,
    ProfilingComponent,
    DataSharingComponent,
    PreferredDealerCustomerUpdateComponent,
    NewConsentVersionDisclaimerComponent,
    LoadingSpinnerComponent,
    BlacklistComponent,
  ]
})
export class CustomerComponent implements OnInit, OnDestroy {
  brand: VehicleBrand;
  customerConsentResponse: CustomerClientResponse;
  customerConsentRequest: CustomerClientRequest;
  showSaveButtons = false;
  newConsentVersionAvailable = false;
  favIcon: HTMLLinkElement = document.querySelector('#favIcon');
  editPreferredDealerMode = false;
  editPreferredDealerLexusMode = false;

  dirty = false;
  willDenyCC = false;
  willDenyCE = false;
  noPreferredDealerError = false;
  noPreferredDealerLexusError = false;

  typedText = '';
  isMobile = window.innerWidth < 640;
  legalEntity = LegalEntity.ToyotaGermany

  @ViewChildren(GroupDealerComponent) groupDealerComponents: QueryList<GroupDealerComponent>;

  protected readonly PageSize = PageSize;

  constructor(
    private customerService: CustomerService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.route.params.subscribe((params) => {
      this.setCredentials(params['credentials']);
      this.setBrand(params['brand']);
    });
  }

  ngOnInit(): void {
    this.setCredentials(this.route.snapshot.params['credentials']);
    this.setBrand(this.route.snapshot.params['brand']);

    // For desktop browsers
    window.addEventListener('beforeunload', this.beforeUnloadListener);

    // Manually add listeners for mobile browsers
    document.addEventListener(
      'visibilitychange',
      this.visibilityChangeListener,
    );
    window.addEventListener('pagehide', this.pageHideListener);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.beforeUnloadListener);
    document.removeEventListener(
      'visibilitychange',
      this.visibilityChangeListener,
    );
    window.removeEventListener('pagehide', this.pageHideListener);
  }

  @HostListener('window:resize')
  onResize() {
    this.isMobile = window.innerWidth < 640;
  }

  private beforeUnloadListener = (event: BeforeUnloadEvent) => {
    if (this.showSaveButtons && this.dirty) {
      event.preventDefault();
      return 'You have unsaved changes. Do you really want to leave?';
    }
    return null;
  };

  private visibilityChangeListener = () => {
    if (document.visibilityState === 'hidden' && this.showSaveButtons && this.dirty) {
      alert('You have unsaved changes. Do you really want to leave?');
    }
  };

  private pageHideListener = (event: PageTransitionEvent) => {
    if (this.showSaveButtons && this.dirty) {
      event.preventDefault();
      alert('You have unsaved changes. Do you really want to leave?');
    }
  };

  setBrand(brand: string) {
    this.brand = new VehicleBrand(brand);
    this.legalEntity = LegalEntity.from(VehicleBrand.from(brand), Country.from(environment.country));
    AppState.shared.setLegalEntity(this.legalEntity);
    this.favIcon.href = '/assets/images/' + this.brand.favicon;
    this.reload();
  }

  setCredentials(credentials: string) {
    if (credentials && credentials.length > 10) {
      localStorage.setItem('credentials', credentials);
    }
  }

  mapCustomerConsentResponseToRequest(response: CustomerClientResponse): CustomerClientRequest {
    const request = new CustomerClientRequest();
    request.preferredDealer = response.preferredDealer;
    request.preferredDealerLexus = response.preferredDealerLexus;
    request.groupDealerList = response.groupDealerList.map(d => ({ ...d }));
    request.connectedDealerList = response.connectedDealerList.map(d => ({ ...d }));
    request.events = response.events;
    request.offers = response.offers;
    request.surveys = response.surveys;
    request.reminder = response.reminder;
    request.post = response.post;
    request.email = response.email;
    request.phone = response.phone;
    request.messenger = response.messenger;
    request.dataSharing = response.dataSharing;
    request.profiling = response.profiling;
    return request;
  }

  reload() {
    if (this.brand) {
      this.customerService.getCustomerConsent(this.brand).subscribe({
        next: (response) => {
          console.log('RESPONSE', response);
          this.customerConsentResponse = plainToInstance(CustomerClientResponse, response);
          AppState.shared.setUserName(this.customerConsentResponse.name)
          AppState.shared.setConsentVersion(this.customerConsentResponse.consentVersion);
          this.customerConsentRequest = this.mapCustomerConsentResponseToRequest(this.customerConsentResponse);
          if (!this.customerConsentResponse) {
            console.log('no consent found');
            this.redirectToErrorPage();
          }
          if (this.customerConsentResponse.consentVersion !== this.customerConsentResponse.latestConsentVersion) {
            this.newConsentVersionAvailable = true;
          }
        },
        error: (error) => {
          console.log('getCustomer error', error);
          this.redirectToErrorPage();
        }
      });
    }
  }

  redirectToErrorPage() {
    this.router.navigate(['error']);
  }

  getContactEvent(key: string) {
    if (!this.customerConsentResponse?.contactEventList) {
      return null;
    }

    for (const e of this.customerConsentResponse.contactEventList) {
      if (e.eventName === key) {
        return e;
      }
    }
    return null;
  }

  onEditPreferredDealerMode() {
    this.editPreferredDealerMode = true;
    if (!this.editPreferredDealerMode) {
      this.validate();
    }
  }

  offEditPreferredDealerMode() {
    this.editPreferredDealerMode = false;
    this.validate();
  }

  onEditPreferredDealerLexusMode() {
    this.editPreferredDealerLexusMode = true;
    if (!this.editPreferredDealerLexusMode) {
      this.validate();
    }
  }

  offEditPreferredDealerLexusMode() {
    this.editPreferredDealerLexusMode = false;
    this.validate();
  }

  mapCustomerConsentRequestToResponse(request: CustomerClientRequest, response: CustomerClientResponse): CustomerClientResponse {
    const newResponse = new CustomerClientResponse();
    newResponse.toyota = response.toyota;
    newResponse.lexus = response.lexus;
    newResponse.name = response.name;
    newResponse.contactEventList = response.contactEventList;
    newResponse.contactChannelList = response.contactChannelList;
    newResponse.preferredDealer = request.preferredDealer;
    newResponse.preferredDealerLexus = request.preferredDealerLexus;
    newResponse.groupDealerList = request.groupDealerList;
    newResponse.allDealerList = response.allDealerList;
    newResponse.requestingDealer = response.requestingDealer;
    newResponse.consentVersion = response.consentVersion;
    newResponse.latestConsentVersion = response.latestConsentVersion;
    newResponse.events = request.events;
    newResponse.offers = request.offers;
    newResponse.surveys = request.surveys;
    newResponse.reminder = request.reminder;
    newResponse.post = request.post;
    newResponse.email = request.email;
    newResponse.phone = request.phone;
    newResponse.messenger = request.messenger;
    newResponse.dataSharing = request.dataSharing;
    newResponse.profiling = request.profiling;
    return newResponse;
  }

  save() {
    this.unlockWindow();
    this.customerConsentResponse = this.mapCustomerConsentRequestToResponse(this.customerConsentRequest, this.customerConsentResponse);
    this.showSaveButtons = false;
    this.willDenyCC = false;
    this.willDenyCE = false;

    if (this.dirty) {
      this.customerService
        .updateCustomerConsent(
          this.brand,
          JSON.parse(JSON.stringify(this.customerConsentRequest)),
        )
        .subscribe((success) => {
          console.log(success);
          this.dirty = false;
          this.reload();
        });
    }
    // Exit edit mode after saving
    this.showSaveButtons = false;
    this.typedText = '';
  }

  cancel() {
    console.log(this.willDenyCC);
    console.log(this.willDenyCE);
    this.unlockWindow();
    this.showSaveButtons = false;
    this.editPreferredDealerMode = false;
    this.editPreferredDealerLexusMode = false;
    this.willDenyCE = false;
    this.willDenyCC = false;
    this.customerConsentRequest = this.mapCustomerConsentResponseToRequest(this.customerConsentResponse);
    console.log('cancel');
    this.dirty = false;
    this.reload();
    this.typedText = '';
  }

  private lockWindowListener(e: any) {
    // Cancel the event as stated by the standard.
    e.preventDefault();
    // Chrome requires returnValue to be set.
    e.returnValue = '';
  }

  unlockWindow() {
    window.removeEventListener('beforeunload', this.lockWindowListener);
  }

  validate() {
    this.dirty = false;

    // Check for changes in contact channels
    if (this.customerConsentRequest.messenger !== this.customerConsentResponse.messenger ||
      this.customerConsentRequest.email !== this.customerConsentResponse.email ||
    this.customerConsentRequest.phone !== this.customerConsentResponse.phone ||
    this.customerConsentRequest.post !== this.customerConsentResponse.post) {
      this.dirty = true;
    }

    // Check for changes in contact events
    if (this.customerConsentRequest.offers !== this.customerConsentResponse.offers ||
      this.customerConsentRequest.events !== this.customerConsentResponse.events ||
      this.customerConsentRequest.surveys !== this.customerConsentResponse.surveys ||
      this.customerConsentRequest.reminder !== this.customerConsentResponse.reminder) {
      this.dirty = true;
    }

    // check changes in a dealer list
    const originalDealerList = this.customerConsentResponse.groupDealerList.map(
      (dealer) => dealer.dealerNumber
    );
    const editedDealerList = this.customerConsentRequest.groupDealerList.map(
      (dealer) => dealer.dealerNumber
    );

    // Sort both arrays to ensure they are comparable
    originalDealerList.sort();
    editedDealerList.sort();

    // Compare the sorted arrays
    if (
      !this.dirty &&
      (originalDealerList.length !== editedDealerList.length ||
        !originalDealerList.every(
          (value, index) => value === editedDealerList[index]
        ))
    ) {
      this.dirty = true;
    }

    // compare preferred dealer Toyota
    const originalPref = this.customerConsentResponse.preferredDealer;
    const editedPref = this.customerConsentRequest.preferredDealer;
    if (JSON.stringify(originalPref) !== JSON.stringify(editedPref)) {
      this.dirty = true;
    }

    // compare preferred dealer Lexus
    const originalPrefLexus = this.customerConsentResponse.preferredDealerLexus;
    const editedPrefLexus = this.customerConsentRequest.preferredDealerLexus;
    if (JSON.stringify(originalPrefLexus) !== JSON.stringify(editedPrefLexus)) {
      this.dirty = true;
    }

    // check profiling
    if (this.customerConsentRequest.profiling !== this.customerConsentResponse.profiling) {
      this.dirty = true;
    }

    // check data sharing
    if (this.customerConsentRequest.dataSharing !== this.customerConsentResponse.dataSharing) {
      this.dirty = true;
    }

    this.showSaveButtons = this.dirty;

    // check if at least one contactChannel and contactEvent have value true
    this.willDenyCC = false;
    this.willDenyCE = false;

    if (this.customerConsentRequest.messenger || this.customerConsentRequest.email ||
      this.customerConsentRequest.phone || this.customerConsentRequest.post) {
      this.willDenyCC = true;
    }

    if (this.customerConsentRequest.offers || this.customerConsentRequest.reminder ||
      this.customerConsentRequest.surveys || this.customerConsentRequest.events) {
      this.willDenyCE = true;
    }

    // check preferred dealer errors
    const connectedToyotaDealers = this.customerConsentRequest.connectedDealerList.filter(
      dealer => dealer.toyota
    );
    this.noPreferredDealerError = false;
    if (this.customerConsentResponse.toyota && this.customerConsentRequest.preferredDealer === null && connectedToyotaDealers.length > 0) {
      this.noPreferredDealerError = true;
    }

    const connectedLexusDealers = this.customerConsentRequest.connectedDealerList.filter(
      dealer => dealer.lexus
    );
    this.noPreferredDealerLexusError = false;
    if (this.customerConsentResponse.lexus && this.customerConsentRequest.preferredDealerLexus === null && connectedLexusDealers.length > 0) {
      this.noPreferredDealerLexusError = true;
    }
  }

  onDealerAdded(dealerWithGroup: DealerWithGroup) {
    this.groupDealerComponents.forEach((c) => c.onDealerAdded(dealerWithGroup));
  }

  shouldGetEventsName() {
    return this.customerConsentResponse.consentVersion !== 'E-2026-1' &&
      this.customerConsentResponse.consentVersion !== 'A-2026-1';
  }

  getCommunicationDescriptionText() {
    return '<br>' + [
      this.shouldGetEventsName() ? '<b>' + this.getContactEvent('OFFERS')?.displayName + '</b>: ' : '',
      this.getContactEvent('OFFERS')?.customerDescription + '<br><br>',
      this.shouldGetEventsName() ? '<b>' + this.getContactEvent('REMINDER')?.displayName + '</b>: ' : '',
      this.getContactEvent('REMINDER')?.customerDescription + '<br><br>',
      this.shouldGetEventsName() ? '<b>' + this.getContactEvent('EVENTS')?.displayName + '</b>: ' : '',
      this.getContactEvent('EVENTS')?.customerDescription + '<br><br>',
      this.shouldGetEventsName() ? '<b>' + this.getContactEvent('SURVEYS')?.displayName + '</b>: ' : '',
      this.getContactEvent('SURVEYS')?.customerDescription
    ].join('');
  }
}
